import React, { useEffect, useRef } from 'react';
import { useAds } from '../../contexts/AdContext';
import { useLanguage } from '../../contexts/LanguageContext';
import './ConsentConfirmationDialog.css';

/**
 * Issue #275: Confirmation popover after accepting or denying personalized ads
 * Shows positive or negative confirmation message based on user's decision
 */
function ConsentConfirmationDialog() {
  const { showConfirmation, confirmationType, dismissConfirmation } = useAds();
  const { t } = useLanguage();
  const buttonRef = useRef(null);

  // Focus the button when dialog opens
  useEffect(() => {
    if (showConfirmation && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, [showConfirmation]);

  // Handle keyboard escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showConfirmation) {
        dismissConfirmation();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showConfirmation, dismissConfirmation]);

  if (!showConfirmation) return null;

  const isPositive = confirmationType === 'positive';
  const message = isPositive
    ? t('ads.confirmation.positive')
    : t('ads.confirmation.negative');

  return (
    <div
      className="consent-confirmation-overlay"
      role="presentation"
    >
      <div
        className="consent-confirmation-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="consent-confirmation-message"
      >
        {/* Confirmation message */}
        <p id="consent-confirmation-message" className="consent-confirmation-message">
          {message}
        </p>

        {/* OK button */}
        <button
          ref={buttonRef}
          className="consent-confirmation-button"
          onClick={dismissConfirmation}
          type="button"
          aria-label={t('ok')}
        >
          {t('ok')}
        </button>
      </div>
    </div>
  );
}

export default ConsentConfirmationDialog;
